/**
 * Enums compartilhados entre API e Web.
 * Mantidos como const objects (não enum do TS) para serem serializáveis e
 * utilizáveis em schemas Zod com z.nativeEnum / z.enum.
 */

export const Channel = {
  META: 'META',
  GOOGLE: 'GOOGLE',
} as const;
export type Channel = (typeof Channel)[keyof typeof Channel];

export const SocialNetwork = {
  INSTAGRAM: 'INSTAGRAM',
  FACEBOOK: 'FACEBOOK',
  LINKEDIN: 'LINKEDIN',
} as const;
export type SocialNetwork = (typeof SocialNetwork)[keyof typeof SocialNetwork];

export const AudienceSegment = {
  RESIDENCIAL: 'RESIDENCIAL',
  EMPRESARIAL: 'EMPRESARIAL',
} as const;
export type AudienceSegment = (typeof AudienceSegment)[keyof typeof AudienceSegment];

export const CampaignObjective = {
  LEADS: 'LEADS',
  TRAFFIC: 'TRAFFIC',
  CONVERSIONS: 'CONVERSIONS',
  MESSAGES: 'MESSAGES',
  CALLS: 'CALLS',
  AWARENESS: 'AWARENESS',
  SALES: 'SALES',
} as const;
export type CampaignObjective = (typeof CampaignObjective)[keyof typeof CampaignObjective];

export const ConversionDestination = {
  LEAD_FORM: 'LEAD_FORM',
  WHATSAPP: 'WHATSAPP',
  PHONE_CALL: 'PHONE_CALL',
  WEBSITE: 'WEBSITE',
} as const;
export type ConversionDestination =
  (typeof ConversionDestination)[keyof typeof ConversionDestination];

export const EntityStatus = {
  DRAFT: 'DRAFT',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  APPROVED: 'APPROVED',
  PUBLISHING: 'PUBLISHING',
  PAUSED: 'PAUSED',
  ACTIVE: 'ACTIVE',
  REJECTED: 'REJECTED',
  ARCHIVED: 'ARCHIVED',
  ERROR: 'ERROR',
} as const;
export type EntityStatus = (typeof EntityStatus)[keyof typeof EntityStatus];

export const SocialPostStatus = {
  DRAFT: 'DRAFT',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  APPROVED: 'APPROVED',
  SCHEDULED: 'SCHEDULED',
  PUBLISHING: 'PUBLISHING',
  PUBLISHED: 'PUBLISHED',
  FAILED: 'FAILED',
  CANCELED: 'CANCELED',
} as const;
export type SocialPostStatus = (typeof SocialPostStatus)[keyof typeof SocialPostStatus];

export const ContentStatus = {
  IDEA: 'IDEA',
  DRAFT: 'DRAFT',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  APPROVED: 'APPROVED',
  SCHEDULED: 'SCHEDULED',
  PUBLISHED: 'PUBLISHED',
  FAILED: 'FAILED',
  ARCHIVED: 'ARCHIVED',
} as const;
export type ContentStatus = (typeof ContentStatus)[keyof typeof ContentStatus];

export const ApprovalStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  CANCELED: 'CANCELED',
} as const;
export type ApprovalStatus = (typeof ApprovalStatus)[keyof typeof ApprovalStatus];

export const ApprovalTargetType = {
  CAMPAIGN_PROJECT: 'CAMPAIGN_PROJECT',
  CREATIVE: 'CREATIVE',
  SOCIAL_POST: 'SOCIAL_POST',
  CONTENT_POST: 'CONTENT_POST',
  RECOMMENDATION: 'RECOMMENDATION',
  BUDGET_CHANGE: 'BUDGET_CHANGE',
} as const;
export type ApprovalTargetType = (typeof ApprovalTargetType)[keyof typeof ApprovalTargetType];

export const IntegrationProvider = {
  META: 'META',
  GOOGLE_ADS: 'GOOGLE_ADS',
  LINKEDIN: 'LINKEDIN',
  OPENAI: 'OPENAI',
  SITE_CONNECTOR: 'SITE_CONNECTOR',
} as const;
export type IntegrationProvider = (typeof IntegrationProvider)[keyof typeof IntegrationProvider];

export const IntegrationStatus = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTED: 'CONNECTED',
  EXPIRED: 'EXPIRED',
  ERROR: 'ERROR',
} as const;
export type IntegrationStatus = (typeof IntegrationStatus)[keyof typeof IntegrationStatus];

export const CreativeFormat = {
  FEED_PORTRAIT: 'FEED_PORTRAIT', // 1080x1350
  FEED_SQUARE: 'FEED_SQUARE', // 1080x1080
  STORY: 'STORY', // 1080x1920
  LANDSCAPE: 'LANDSCAPE', // 1200x628
  BANNER_WEB: 'BANNER_WEB', // 1440x600
  BLOG_COVER: 'BLOG_COVER', // 1200x675
} as const;
export type CreativeFormat = (typeof CreativeFormat)[keyof typeof CreativeFormat];

export const CREATIVE_FORMAT_DIMENSIONS: Record<CreativeFormat, { width: number; height: number }> =
  {
    FEED_PORTRAIT: { width: 1080, height: 1350 },
    FEED_SQUARE: { width: 1080, height: 1080 },
    STORY: { width: 1080, height: 1920 },
    LANDSCAPE: { width: 1200, height: 628 },
    BANNER_WEB: { width: 1440, height: 600 },
    BLOG_COVER: { width: 1200, height: 675 },
  };

export const LeadStage = {
  LEAD: 'LEAD',
  ATTENDED: 'ATTENDED',
  QUALIFIED: 'QUALIFIED',
  HAS_COVERAGE: 'HAS_COVERAGE',
  CONTRACT: 'CONTRACT',
  INSTALLED: 'INSTALLED',
  LOST: 'LOST',
} as const;
export type LeadStage = (typeof LeadStage)[keyof typeof LeadStage];

export const LEAD_STAGE_ORDER: LeadStage[] = [
  'LEAD',
  'ATTENDED',
  'QUALIFIED',
  'HAS_COVERAGE',
  'CONTRACT',
  'INSTALLED',
];

export const RecommendationSeverity = {
  INFO: 'INFO',
  WARNING: 'WARNING',
  CRITICAL: 'CRITICAL',
} as const;
export type RecommendationSeverity =
  (typeof RecommendationSeverity)[keyof typeof RecommendationSeverity];

export const AutomationLevel = {
  RECOMMEND_ONLY: 'RECOMMEND_ONLY',
  REQUIRES_APPROVAL: 'REQUIRES_APPROVAL',
  CONTROLLED_AUTOMATION: 'CONTROLLED_AUTOMATION',
} as const;
export type AutomationLevel = (typeof AutomationLevel)[keyof typeof AutomationLevel];

export const ConsentBasis = {
  CONSENT: 'CONSENT',
  LEGITIMATE_INTEREST: 'LEGITIMATE_INTEREST',
  CONTRACT: 'CONTRACT',
} as const;
export type ConsentBasis = (typeof ConsentBasis)[keyof typeof ConsentBasis];
